<template>
  <div class="create-voice-wrapper">
    <div class="content-area">
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-content">
          <div class="header-left">
            <v-btn
              icon
              text
              class="back-btn"
              @click="goBack">
              <Icon name="arrow-left" variant="solid" size="20" color="default"/>
            </v-btn>
            <h1 class="page-title">
              Create Voice
            </h1>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="main-content main-content--split-row">
        <!-- Left Panel -->
        <div class="left-panel">
          <div class="form-content">
            <!-- Voice Name -->
            <div class="form-group">
              <label class="form-label required">
                Voice Name
                <span class="required-asterisk">*</span>
              </label>
              <MankTextField
                v-model="formData.voiceName"
                placeholder="Enter Voice Name"
                class="form-input"
                :error-messages="errors.voiceName"
                hide-details="auto"/>
            </div>

            <!-- Info + Drag Area -->
            <div class="upload-section">
              <!-- Info Line -->
              <div class="info-line">
                <div class="info-icon">
                  <Icon name="info-circle" variant="solid" size="16" color="info"/>
                </div>
                <span class="info-text">
                  Upload at least one high quality audio recording to make a new voice.
                </span>
              </div>

              <!-- Drag & Drop Area -->
              <div
                class="drag-drop-area"
                :class="{ 'drag-over': isDragOver, 'has-file': formData.audioFile }"
                @dragover.prevent="handleDragOver"
                @dragleave.prevent="handleDragLeave"
                @drop.prevent="handleDrop"
                @click="!formData.audioFile && triggerFileUpload()">
                <!-- Empty State -->
                <div v-if="!formData.audioFile" class="empty-state">
                  <!-- Voice Icon -->
                  <div class="voice-icon">
                    <div class="icon-circle">
                      <Icon name="microphone" variant="solid" size="24" color="primary"/>
                    </div>
                  </div>

                  <!-- Text Content -->
                  <div class="empty-text">
                    <h3 class="empty-title">
                      Drag & Drop or Upload Audio File
                    </h3>
                    <p class="empty-description">
                      10mb limit, mp3, wav, aac, flac files accepted
                    </p>
                  </div>

                  <!-- Upload Button -->
                  <div class="upload-button">
                    <v-btn
                      outlined
                      color="primary"
                      small
                      class="upload-btn"
                      @click.stop="triggerFileUpload">
                      <Icon name="upload" variant="solid" size="20" color="primary"/>
                      <span class="upload-btn-label">Upload File</span>
                    </v-btn>
                  </div>
                </div>

                <!-- File Selected State -->
                <div v-else class="file-uploaded-state">
                  <!-- Voice Icon -->
                  <div class="voice-icon">
                    <div class="icon-circle">
                      <Icon name="microphone" variant="solid" size="24" color="primary"/>
                    </div>
                  </div>

                  <!-- Text Content -->
                  <div class="upload-text">
                    <h3 class="upload-title">
                      1 File Uploaded
                    </h3>

                    <!-- File Tags -->
                    <div class="file-tags">
                      <div class="file-tag">
                        <span class="tag-label">{{ formData.audioFile.name }}</span>
                        <div class="remove-tag-btn" @click.stop="removeFile">
                          <Icon name="cross" variant="solid" size="12" color="default"/>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Upload More Button -->
                  <div class="upload-buttons">
                    <v-btn
                      outlined
                      color="primary"
                      small
                      class="upload-more-btn"
                      @click.stop="triggerFileUpload">
                      <Icon name="upload" variant="solid" size="20" color="primary"/>
                      <span class="upload-btn-label">Upload File</span>
                    </v-btn>
                  </div>
                </div>

                <!-- Hidden File Input -->
                <input
                  ref="fileInput"
                  type="file"
                  accept=".mp3,.wav,.aac,.flac"
                  style="display: none"
                  @change="handleFileSelect">
              </div>

              <!-- Audio File Error -->
              <div v-if="errors.audioFile.length > 0" class="error-messages">
                <p v-for="error in errors.audioFile" :key="error" class="error-message">
                  {{ error }}
                </p>
              </div>
            </div>

            <!-- Description -->
            <div class="form-group">
              <label class="form-label">Description</label>
              <MankTextarea
                v-model="formData.description"
                placeholder="What are the specifics of this voice? You can include language, age, gender etc."
                variant="outlined"
                :error-messages="errors.description"
                hide-details="auto"
                rows="4"
                class="description-textarea"/>
              <div class="helper-text" :class="{ 'error-text': formData.description.length > 300 }">
                {{ formData.description.length }}/300
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div class="footer-buttons">
        <v-btn
          text
          class="cancel-btn"
          @click="cancelCreate">
          <Icon name="cross" variant="solid" size="20" color="default"/>
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          class="create-btn"
          elevation="0"
          :loading="loading"
          :disabled="!isFormValid || loading"
          @click="createVoice">
          <Icon name="plus" variant="solid" size="20" color="onPrimary"/>
          Create Voice
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/Icon.vue';
import VoiceService from '@/services/VoiceService';

export default {
  name: 'CreateVoice',
  components: { Icon },
  data() {
    return {
      loading: false,
      isDragOver: false,
      formData: {
        voiceName: '',
        description: '',
        audioFile: null, // Single file olarak değiştirildi
      },
      errors: {
        voiceName: [],
        description: [],
        audioFile: [],
      },
    };
  },
  computed: {
    isFormValid() {
      return this.formData.voiceName.trim() !== '' && this.formData.audioFile !== null;
    },
  },
  methods: {
    goBack() {
      this.$router.go(-1);
    },
    handleDragOver(event) {
      event.preventDefault();
      this.isDragOver = true;
    },
    handleDragLeave(event) {
      event.preventDefault();
      this.isDragOver = false;
    },
    handleDrop(event) {
      event.preventDefault();
      this.isDragOver = false;
      const files = Array.from(event.dataTransfer.files);
      this.processFiles(files);
    },
    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.processFiles(files);
    },
    processFiles(files) {
      if (files.length === 0) return;

      const file = files[0]; // Sadece ilk dosyayı al
      const validTypes = ['audio/mp3', 'audio/wav', 'audio/aac', 'audio/flac', 'audio/mpeg'];
      const maxSize = 10 * 1024 * 1024; // 10MB

      // Hataları temizle
      this.clearErrors();

      // Dosya türü kontrolü
      if (!validTypes.includes(file.type)) {
        this.errors.audioFile = ['Please select a valid audio file (mp3, wav, aac, flac)'];
        return;
      }

      // Dosya boyutu kontrolü
      if (file.size > maxSize) {
        this.errors.audioFile = ['File size must be less than 10MB'];
        return;
      }

      // Dosyayı kaydet
      this.formData.audioFile = file;
      this.$store.dispatch('ui/showNotification', {
        type: 'success',
        title: 'File Added',
        message: `${file.name} added successfully`,
      });
    },
    cancelCreate() {
      this.$router.push({ name: 'Voices' });
    },
    async createVoice() {
      this.loading = true;
      this.clearErrors();

      try {
        // Form validasyonu
        if (!this.validateForm()) {
          this.loading = false;
          return;
        }

        // FormData oluştur (multipart/form-data için)
        const formData = VoiceService.createVoiceFormData(
          {
            name: this.formData.voiceName.trim(),
            description: this.formData.description.trim(),
          },
          this.formData.audioFile,
        );

        // API çağrısı
        await VoiceService.createVoice(formData);

        this.$store.dispatch('ui/showNotification', {
          type: 'success',
          title: 'Success',
          message: 'Voice created successfully',
        });

        this.$router.push({ name: 'Voices' });
      } catch (error) {
        let errorMessage = 'Failed to create voice';

        // API error handling
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        this.$store.dispatch('ui/showNotification', {
          type: 'error',
          title: 'Error',
          message: errorMessage,
        });
      } finally {
        this.loading = false;
      }
    },
    clearErrors() {
      this.errors = {
        voiceName: [],
        description: [],
        audioFile: [],
      };
    },
    validateForm() {
      let isValid = true;

      // Voice name validation
      if (!this.formData.voiceName.trim()) {
        this.errors.voiceName = ['Voice name is required'];
        isValid = false;
      }

      // Description validation (optional but max 300 chars)
      if (this.formData.description && this.formData.description.length > 300) {
        this.errors.description = ['Description cannot exceed 300 characters'];
        isValid = false;
      }

      // Audio file validation
      if (!this.formData.audioFile) {
        this.errors.audioFile = ['Audio file is required'];
        isValid = false;
      }

      return isValid;
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / (k ** i)).toFixed(2)) + ' ' + sizes[i];
    },
    removeFile() {
      this.formData.audioFile = null;
      this.clearErrors();
    },
    triggerFileUpload() {
      if (!this.formData.audioFile) {
        this.$refs.fileInput.click();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/variables.scss";
@import "../styles/colors.scss";

.main-content {
  overflow: hidden;
}

.left-panel {
  flex: 1;
  overflow-y: auto;

  .form-content {
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .form-label {
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    color: $light-foreground-1;
    display: flex;
    align-items: center;
    gap: 4px;

    &.required {
      .required-asterisk {
        color: $light-primary;
        font-weight: 700;
      }
    }
  }

  .helper-text {
    font-family: $body-font-family;
    font-size: $caption-1-size;
    font-weight: 400;
    color: $light-foreground-4;
    text-align: right;

    &.error-text {
      color: #f44336;
    }
  }
}

.upload-section {
  display: flex;
  flex-direction: column;
  gap: 12px;

  .info-line {
    display: flex;
    align-items: center;
    gap: 8px;

    .info-icon {
      width: 24px;
      height: 24px;
      background-color: #EDF3FE;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .info-text {
      font-family: $body-font-family;
      font-size: $body-3-size;
      color: $light-foreground-4;
      line-height: 1.6;
    }
  }

  .drag-drop-area {
    border: 2px dashed $light-border;
    border-radius: 12px;
    background-color: $light-surface-1;
    padding: 12px;
    transition: all 0.2s ease;

  &.drag-over {
    border-color: $light-primary;
    background-color: lighten($light-primary, 45%);
  }

  &.has-file {
    border-color: #10B981;
    background-color: #F0FDF4;
  }

  .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;
      padding: 24px;

      .voice-icon {
        .icon-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background-color: #FDEEEE;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .empty-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 4px;
        text-align: center;

        .empty-title {
          font-family: $body-font-family;
          font-weight: 700;
          font-size: $body-2-size;
          color: $light-foreground-1;
          margin: 0;
          line-height: 1.4;
        }

        .empty-description {
          font-family: $body-font-family;
          font-size: $caption-1-size;
          color: $light-foreground-4;
          margin: 0;
          line-height: 1.6;
        }
      }

      .upload-button {
        .upload-btn {
          font-family: $body-font-family;
          font-weight: 600;
          font-size: $body-3-size;
          text-transform: none;
          letter-spacing: normal;

          &::v-deep .v-btn__content {
            display: inline-flex !important;
            align-items: center;
            gap: 8px;
            color: $light-primary !important;
            opacity: 1 !important;
          }

          &:hover {
            &::v-deep .v-btn__content {
              color: $light-primary !important;
            }
          }
        }

        .upload-btn-label {
          color: inherit;
          font: inherit;
          font-weight: 600;
        }
      }
    }

    .file-uploaded-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      align-self: stretch;
      gap: 12px;
      width: 100%;

      .voice-icon {
        .icon-circle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background-color: $light-surface-2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
      }

      .upload-text {
        display: flex;
        flex-direction: column;
        align-items: center;
        align-self: stretch;
        gap: 8px;

        .upload-title {
          font-family: $body-font-family;
          font-size: $body-2-size;
          font-weight: 700;
          line-height: 1.4em;
          color: $light-foreground-1;
          text-align: center;
          margin: 0;
        }

        .file-tags {
          display: flex;
          align-items: center;
          align-self: stretch;
          flex-wrap: wrap;
          gap: 8px;

          .file-tag {
            display: flex;
            justify-content: center;
            align-items: center;
            gap: 8px;
            padding: 8px 8px 8px 12px;
            background-color: $light-surface-2;
            border-radius: 200px;
            height: 32px;

            .tag-label {
              font-family: $body-font-family;
              font-size: $caption-1-size;
              font-weight: 600;
              line-height: 1.2em;
              color: $light-foreground-1;
              text-align: left;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 150px;
            }

            .remove-tag-btn {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 16px;
              height: 16px;
              background-color: $light-surface-1;
              border-radius: 50%;
              cursor: pointer;
              transition: background-color 0.2s ease;

              &:hover {
                background-color: $light-surface-3;
              }
            }
          }
        }
      }

      .upload-buttons {
        display: flex;
        gap: 8px;

        .upload-more-btn {
          font-family: $body-font-family;
          font-size: $body-3-size;
          font-weight: 600;
          text-transform: none;
          letter-spacing: normal;

          &::v-deep .v-btn__content {
            display: inline-flex !important;
            align-items: center;
            gap: 8px;
            color: $light-primary !important;
            opacity: 1 !important;
          }
        }
      }
    }
  }
}

.footer-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 12px 24px;
  border-top: 1px solid $light-border;
  background-color: $light-surface-2;

  .cancel-btn,
  .create-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 600;
    text-transform: none;
  }

  .cancel-btn {
    background-color: $light-surface-1;
    color: $light-foreground-1;

    &:hover {
      background-color: $light-surface-3;
    }
  }

  .create-btn {
    width: 120px;
    background-color: $light-primary;
    color: $light-surface-2;

    &:hover {
      background-color: darken($light-primary, 10%);
    }
  }
}

.error-messages {
  margin-top: 8px;

  .error-message {
    font-family: $body-font-family;
    font-size: $caption-1-size;
    font-weight: 400;
    color: #f44336;
    margin: 0 0 4px 0;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

// Vuetify overrides
.mank-input,
.mank-textarea {
  :deep(.v-field__field) {
    background-color: $light-surface-1;
  }

  :deep(.v-field__input) {
    color: $light-foreground-1;
    font-family: $body-font-family;
    font-size: $body-3-size;
    font-weight: 400;
  }

  :deep(.v-field__outline) {
    --v-field-border-opacity: 0;
  }
}

.description-textarea {
  :deep(.v-field__field) {
    min-height: 120px;
  }
}
</style>
